import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  ChevronRight,
  Menu, 
  X, 
  ArrowUp, 
  Mail, 
  Phone, 
  MapPin, 
  CheckCircle,
  Linkedin,
  Facebook,
  Calendar,
  BarChart2,
  Shield,
  Zap,
  Settings,
  Wrench
} from 'lucide-react';

const GaloProjectDetailPage = () => {
  // State management
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Project data
  const project = {
    id: 1,
    title: "Underbody & Closures Systems",
    subtitle: "Advanced Automation Integration",
    client: "Ford Motor Company",
    location: "Köln, Germany",
    timeline: "2016-2017",
    category: "Automotive",
    tags: ["Automation", "Robotics", "Welding Systems"],
    description: "Implementation of advanced automation systems for underbody and closures in the B479 project, working with Valiant TMS as contractor.",
    challenge: "Ford Motor Company needed to modernize their manufacturing facility in Köln, Germany to increase production efficiency while maintaining the highest quality standards. The challenge was to implement new automation systems while minimizing production downtime.",
    solution: "Galo Industries developed custom software systems for process control and monitoring while implementing advanced robotics systems for underbody assembly and closures manufacturing. The implementation included specialized welding systems with real-time quality control.",
    results: [
      "30% increase in production efficiency",
      "35% reduction in quality-related errors",
      "98.7% system uptime after implementation",
      "ROI achieved within 18 months of operation"
    ],
    heroImage: "/img/engineering.jpeg",
    stats: [
      { label: "Efficiency Increase", value: "+30%", icon: <BarChart2 className="w-5 h-5" /> },
      { label: "Error Reduction", value: "-35%", icon: <Shield className="w-5 h-5" /> },
      { label: "System Uptime", value: "98.7%", icon: <Zap className="w-5 h-5" /> },
      { label: "Implementation Time", value: "11 months", icon: <Calendar className="w-5 h-5" /> }
    ],
    relatedProjects: [
      {
        id: 2,
        title: "Underbody & Bodyside Integration",
        client: "Ford Motor Company",
        location: "Saarlouis, Germany",
        image: "/img/engineering.jpeg"
      },
      {
        id: 3,
        title: "Framing & Roof Systems",
        client: "Jaguar Land Rover",
        location: "Birmingham, UK",
        image: "/img/engineering.jpeg"
      },
      {
        id: 4,
        title: "L462/L663 Underbody Systems",
        client: "Jaguar Land Rover",
        location: "Nitra, Slovakia",
        image: "/img/engineering.jpeg"
      }
    ]
  };

  // Navigation links
  const navLinks = [
    { id: 'home', label: 'Home', link: '/' },
    { id: 'projects', label: 'Projects', link: '/projects' },
    { id: 'about', label: 'About', link: '/about' },
    { id: 'services', label: 'Services', link: '/services' },
    { id: 'careers', label: 'Careers', link: '/careers' },
    { id: 'blog', label: 'Blog', link: '/blog' },
    { id: 'contact', label: 'Contact', link: '/contact' }
  ];

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="font-sans antialiased overflow-x-hidden bg-white">
      {/* Global Styles */}
      <style jsx global>{`
        :root {
          --primary-blue: #4478c7;
          --dark-blue: #1a365d;
          --light-blue: #e1ebf8;
          --accent-blue: #2c5ca3;
          --deep-blue: #0e2a54;
        }
        
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 0.8; }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse 4s ease-in-out infinite;
        }
        
        .card-hover {
          transition: all 0.3s ease;
        }
        
        .card-hover:hover {
          transform: translateY(-6px);
          box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.15);
        }
      `}</style>
      
      {/* Header / Navigation */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'py-2 bg-white shadow-lg' : 'py-4 bg-white shadow-sm'
        }`}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center z-10">
            <img 
              src="/img/logo.png" 
              alt="GALO Industries"
              className="h-16 transition-opacity duration-300"
            />
          </a>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map(link => (
              <a
                key={link.id}
                href={link.link}
                className={`relative px-2 py-1 text-sm font-medium transition-colors tracking-wide hover:opacity-90 ${
                  link.id === 'projects'
                    ? 'text-blue-800'
                    : 'text-gray-700 hover:text-blue-800'
                }`}
              >
                {link.label}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-current transition-all duration-300 ${
                  link.id === 'projects' ? 'w-full' : 'w-0'
                }`}></span>
              </a>
            ))}
            
            <a 
              href="/contact"
              className="px-5 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-all bg-blue-800 text-white hover:bg-blue-800"
            >
              Get in Touch
            </a>
          </nav>
          
          {/* Mobile Menu Button */}
          <button
            className="lg:hidden z-50 w-10 h-10 flex items-center justify-center"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              <X className="w-6 h-6 text-gray-800" />
            ) : (
              <Menu className="w-6 h-6 text-gray-800" />
            )}
          </button>
        </div>
        
        {/* Mobile Menu */}
        <div
          className={`fixed inset-0 bg-white z-40 transition-transform duration-300 ease-in-out ${
            menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="container mx-auto px-6 pt-20 pb-16 h-full flex flex-col">
            <nav className="flex flex-col space-y-4 mt-4">
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.link}
                  className="text-lg font-medium text-gray-800 hover:text-blue-800 transition-colors py-3 px-4 rounded-lg hover:bg-gray-50"
                >
                  {link.label}
                </a>
              ))}
            </nav>
            
            <div className="mt-auto">
              <a 
                href="/contact"
                className="block w-full py-3.5 bg-blue-800 text-white text-center rounded-lg font-medium hover:bg-blue-800 shadow-md transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </header>
      
      {/* Project Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-800 to-blue-800 text-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,_#fff_1px,_transparent_1px)] bg-[length:20px_20px] opacity-10"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          {/* Breadcrumb */}
          <div className="mb-8">
            <div className="flex items-center text-blue-200 text-sm">
              <a href="/" className="hover:text-white transition-colors">Home</a>
              <ChevronRight className="w-4 h-4 mx-2" />
              <a href="/projects" className="hover:text-white transition-colors">Projects</a>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white font-medium">{project.title}</span>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Project title section */}
              <div className="mb-8">
                <div className="inline-flex items-center mb-4 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                  <span className="text-blue-100 font-medium text-sm">{project.category} / {project.timeline}</span>
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                  {project.title}
                </h1>
                <p className="text-xl text-blue-100 mb-6">{project.subtitle}</p>
                
                <div className="flex flex-wrap gap-3 mb-8">
                  {project.tags.map((tag, index) => (
                    <span key={index} className="bg-blue-800/50 border border-blue-500/30 px-3 py-1 rounded-full text-sm text-blue-100">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div>
                    <div className="text-blue-300 text-sm mb-1">Client</div>
                    <div className="text-white font-medium">{project.client}</div>
                  </div>
                  <div>
                    <div className="text-blue-300 text-sm mb-1">Location</div>
                    <div className="text-white font-medium">{project.location}</div>
                  </div>
                </div>
                
                <a href="#project-details" className="bg-blue-800 hover:bg-blue-800 text-white px-6 py-3 rounded-lg font-medium transition-all flex items-center shadow-lg shadow-blue-950/30 inline-flex">
                  Project Details
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
            
            <div className="relative">
              {/* Project hero image */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src={project.heroImage} 
                  alt={project.title}
                  className="w-full h-80 lg:h-96 object-cover"
                />
                {/* Image overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-blue-800/80 via-transparent to-transparent"></div>
                
                {/* Stats overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="grid grid-cols-2 gap-3">
                    {project.stats.slice(0, 2).map((stat, index) => (
                      <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 p-3 shadow-lg">
                        <div className="flex items-center text-blue-200 mb-1 text-sm">
                          {stat.icon}
                          <span className="ml-2">{stat.label}</span>
                        </div>
                        <div className="text-white text-xl font-bold">{stat.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="w-full">
            <path fill="#ffffff" fillOpacity="1" d="M0,96L48,128C96,160,192,224,288,224C384,224,480,160,576,133.3C672,107,768,117,864,144C960,171,1056,213,1152,208C1248,203,1344,149,1392,122L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>
      
      {/* Project Overview Section */}
      <section id="project-details" className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Project Overview</h2>
              <p className="text-gray-600 mb-10 text-lg leading-relaxed">
                {project.description}
              </p>
              
              <div className="mb-12">
                <h3 className="text-xl font-bold mb-4 text-gray-900">The Challenge</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {project.challenge}
                </p>
                
                <h3 className="text-xl font-bold mb-4 text-gray-900">Our Solution</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {project.solution}
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100">
                <h3 className="text-xl font-bold mb-6 text-gray-900 flex items-center">
                  <Settings className="w-6 h-6 mr-3 text-blue-800" />
                  Key Results
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {project.results.map((result, index) => (
                    <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:border-blue-100 hover:shadow transition-all">
                      <CheckCircle className="w-5 h-5 text-blue-800 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{result}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 sticky top-24">
                <h3 className="text-lg font-bold mb-6 text-gray-900 pb-4 border-b border-gray-100">Project Details</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <Wrench className="w-5 h-5 text-blue-800 mr-3 mt-1" />
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Client</div>
                      <div className="font-medium text-gray-900">{project.client}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-blue-800 mr-3 mt-1" />
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Location</div>
                      <div className="font-medium text-gray-900">{project.location}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Calendar className="w-5 h-5 text-blue-800 mr-3 mt-1" />
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Timeline</div>
                      <div className="font-medium text-gray-900">{project.timeline}</div>
                    </div>
                  </div>
                </div>
                
                <h4 className="text-sm font-bold text-gray-500 uppercase mb-3 tracking-wider">Project Stats</h4>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {project.stats.map((stat, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center text-gray-500 text-xs mb-1">
                        {stat.icon}
                        <span className="ml-1.5">{stat.label}</span>
                      </div>
                      <div className="text-gray-900 font-bold text-lg">{stat.value}</div>
                    </div>
                  ))}
                </div>
                
                <div className="pt-6 border-t border-gray-100">
                  <a 
                    href="/contact" 
                    className="block w-full py-3 bg-blue-800 text-white text-center rounded-lg shadow-md hover:bg-blue-800 transition-colors font-medium"
                  >
                    <span className="flex items-center justify-center">
                      Discuss Your Project
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Project Images */}
          <div className="mt-16 pt-10 border-t border-gray-100">
            <h2 className="text-2xl font-bold mb-8 text-gray-900">Project Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 h-64">
                <img 
                  src={project.heroImage} 
                  alt="Project image 1"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 h-64">
                <img 
                  src={project.heroImage} 
                  alt="Project image 2"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 h-64">
                <img 
                  src={project.heroImage} 
                  alt="Project image 3"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
          
          {/* Related Projects */}
          <div className="mt-20 pt-12 border-t border-gray-200">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Related Projects</h2>
              <a href="/projects" className="text-blue-800 font-medium flex items-center hover:text-blue-800 transition-colors">
                View All Projects
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {project.relatedProjects.map((relProj) => (
                <div 
                  key={relProj.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all border border-gray-100 card-hover"
                >
                  <div className="h-48 relative overflow-hidden">
                    <img
                      src={relProj.image}
                      alt={relProj.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                      <div className="p-6">
                        <span className="text-white text-sm font-medium">{relProj.client}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-blue-800 transition-colors">
                      {relProj.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4">{relProj.location}</p>
                    <a 
                      href={`/projects/${relProj.id}`}
                      className="inline-flex items-center text-blue-800 font-medium hover:text-blue-800 transition-colors group text-sm"
                    >
                      View Project
                      <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle,_#fff_1px,_transparent_1px)] bg-[length:20px_20px] opacity-10"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to transform your industrial processes?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Let our team of automation experts help you design and implement innovative solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact"
                className="bg-white text-blue-800 hover:bg-blue-50 px-8 py-4 rounded-xl text-lg font-medium transition-all inline-flex items-center justify-center shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center">
                  Start a Conversation
                  <ArrowRight className="ml-2 w-5 h-5" />  
                </span>
              </a>
              <a 
                href="/services"
                className="bg-transparent border border-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-xl text-lg font-medium hover:bg-white/10 transition-all inline-flex items-center justify-center"
              >
                <span className="flex items-center">
                  Explore Our Services
                  <ChevronRight className="ml-2 w-5 h-5" />  
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-10 relative">
        <div className="absolute inset-0 bg-[radial-gradient(#2c5ca3_1px,transparent_1px)] bg-[length:20px_20px] opacity-5"></div>
        
        <div className="container mx-auto px-6 relative">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-2">
              <img 
                src="/img/logo-white.png" 
                alt="GALO Industries"
                className="h-16 brightness-0 invert mb-6"
              />
              <p className="text-gray-400 mb-6">
                Leading provider of industrial automation solutions. We transform manufacturing processes with innovative technology and engineering excellence.
              </p>
              
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 rounded bg-gray-800 flex items-center justify-center hover:bg-blue-800 transition-all duration-300">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded bg-gray-800 flex items-center justify-center hover:bg-blue-800 transition-all duration-300">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="font-bold text-xl mb-6">Contact</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300">Šibenska ulica 4B,<br/>23000, Zadar, Croatia</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <a href="mailto:info@galo-industries.com" className="text-gray-300 hover:text-white transition-colors duration-300">
                      info@galo-industries.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="w-5 h-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <a href="tel:+385981991813" className="text-gray-300 hover:text-white transition-colors duration-300">
                      +385 98 199 1813
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-bold text-xl mb-6">Quick Links</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-gray-400 hover:text-white transition-colors duration-300">Home</a></li>
                <li><a href="/services" className="text-gray-400 hover:text-white transition-colors duration-300">Services</a></li>
                <li><a href="/projects" className="text-gray-400 hover:text-white transition-colors duration-300">Projects</a></li>
                <li><a href="/about" className="text-gray-400 hover:text-white transition-colors duration-300">About Us</a></li>
                <li><a href="/contact" className="text-gray-400 hover:text-white transition-colors duration-300">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between">
            <p className="text-gray-500 text-sm mb-6 md:mb-0">
              © 2025 Galo Industries. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="/privacy-policy" className="text-gray-500 hover:text-white transition-colors duration-300">Privacy Policy</a>
              <a href="/terms" className="text-gray-500 hover:text-white transition-colors duration-300">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-12 h-12 bg-blue-800 text-white rounded-xl shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-20 hover:shadow-xl"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default GaloProjectDetailPage;